import { useCheckbox, useCheckboxGroupItem } from '@react-aria/checkbox'
import { useFocusRing } from '@react-aria/focus'
import { mergeProps, useId } from '@react-aria/utils'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import React from 'react'
import type { CheckboxGroupState } from 'react-stately'
import { useToggleState } from 'react-stately'
import { CheckboxGroupContext } from '../CheckboxGroup/CheckboxGroup'
import { StyledActions } from '../Content/Content.styles'
import {
  StyledFooter,
  StyledHeroMedia,
  StyledHeroOverlay,
  StyledHeroText,
  StyledInteractableTile,
  StyledLinkTile,
  StyledSelectableTile,
  StyledTileContent,
} from '../Tile/Tile.styles'
import { addSubcomponents } from '../_utils/addSubcomponents'
import { mergeRefs } from '../_utils/mergeRefs'
import type { Props } from '../_utils/types'
import type {
  CheckboxTileProps,
  HeroMediaProps,
  HeroTextProps,
  InteractableTileProps,
  LinkTileProps,
  Roles,
  TileCommonProps,
  TileProps,
  TileRef,
} from './NextTile.types'

const heroTextGridTemplateColumns = 'auto 1fr'

const TileContext = React.createContext<
  Pick<TileCommonProps<'none'>, 'orientation'>
>({
  orientation: 'portrait',
})

const HeroMedia = React.forwardRef<HTMLDivElement, HeroMediaProps>(
  function HeroMedia(
    { children, overlay = false, onOverlayClick, ...props },
    ref
  ) {
    const { orientation } = React.useContext(TileContext)
    const heroRef = React.useRef<HTMLDivElement>(null)

    React.useLayoutEffect(() => {
      if (orientation === 'landscape') {
        heroRef.current?.parentElement?.style.removeProperty(
          '--core-tile-grid-template-columns'
        )
      }
    }, [orientation])

    return (
      <StyledHeroMedia
        ref={mergeRefs(heroRef, ref)}
        $orientation={orientation}
        {...props}
      >
        {overlay && <StyledHeroOverlay onClick={onOverlayClick} />}
        {children}
      </StyledHeroMedia>
    )
  }
)

const HeroText = React.forwardRef<HTMLDivElement, HeroTextProps>(
  function HeroText({ children, ...props }, ref) {
    const { orientation } = React.useContext(TileContext)
    const heroRef = React.useRef<HTMLDivElement>(null)

    React.useLayoutEffect(() => {
      if (orientation === 'landscape') {
        heroRef.current?.parentElement?.style.setProperty(
          '--core-tile-grid-template-columns',
          heroTextGridTemplateColumns
        )
      }
    }, [orientation])

    return (
      <StyledHeroText
        ref={mergeRefs(heroRef, ref)}
        $orientation={orientation}
        {...props}
      >
        {children}
      </StyledHeroText>
    )
  }
)

const Content = React.forwardRef<HTMLDivElement, HeroTextProps>(
  function Content(props, ref) {
    return <StyledTileContent ref={ref} {...props} />
  }
)

const Footer = React.forwardRef<HTMLDivElement, Props>(function Footer(
  { children, ...props },
  ref
) {
  return (
    <StyledFooter ref={ref} {...props}>
      {children}
    </StyledFooter>
  )
})

const Actions = React.forwardRef<HTMLDivElement, Props>(function Actions(
  { children, ...props },
  ref
) {
  return (
    <StyledActions ref={ref} {...props}>
      {children}
    </StyledActions>
  )
})

export const InteractableTile = React.forwardRef<
  HTMLDivElement,
  Omit<InteractableTileProps, 'role'>
>(function InteractableTile({ orientation, children, ...props }, ref) {
  return (
    <StyledInteractableTile ref={ref} $orientation={orientation} {...props}>
      <TileContext.Provider value={{ orientation }}>
        {children}
      </TileContext.Provider>
    </StyledInteractableTile>
  )
})

export const LinkTile = React.forwardRef<
  HTMLAnchorElement,
  Omit<LinkTileProps, 'role'>
>(function LinkTile({ orientation, children, ...props }, ref) {
  return (
    <StyledLinkTile ref={ref} $orientation={orientation} {...props}>
      <TileContext.Provider value={{ orientation }}>
        {children}
      </TileContext.Provider>
    </StyledLinkTile>
  )
})

// Shared render component
const CheckboxTileRender: React.FC<{
  inputProps: React.InputHTMLAttributes<HTMLInputElement>
  focusProps: React.HTMLAttributes<HTMLInputElement>
  inputRef: React.RefObject<HTMLInputElement>
  forwardedRef: React.ForwardedRef<HTMLInputElement>
  isDisabled: boolean
  isSelected: boolean
  isFocusVisible: boolean
  orientation?: 'portrait' | 'landscape'
  className?: string
  style?: React.CSSProperties
  role?: string
  'aria-labelledby'?: string
  children?: React.ReactNode
}> = ({
  inputProps,
  focusProps,
  inputRef,
  forwardedRef,
  isDisabled,
  isSelected,
  isFocusVisible,
  orientation,
  className,
  style,
  role,
  'aria-labelledby': ariaLabelledby,
  children,
}) => {
  const fallbackId = useId()

  return (
    <StyledSelectableTile
      $disabled={isDisabled}
      $selected={isSelected}
      $focused={isFocusVisible}
      $orientation={orientation ?? 'portrait'}
      $layoutless
      className={className}
      style={style}
    >
      <div
        role={role ?? 'checkbox'}
        aria-labelledby={ariaLabelledby ?? fallbackId}
        aria-checked={isSelected}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        onClick={() => {
          if (!isDisabled) {
            inputRef.current?.click()
          }
        }}
        onKeyDown={(e) => {
          if (e.code === 'Space' || e.code === 'Enter') {
            e.preventDefault()
            if (!isDisabled) {
              inputRef.current?.click()
            }
          }
        }}
      >
        <VisuallyHidden>
          <input
            {...mergeProps(inputProps, focusProps)}
            ref={mergeRefs(inputRef, forwardedRef)}
            tabIndex={-1}
          />
        </VisuallyHidden>
        <TileContext.Provider
          value={{ orientation: orientation ?? 'portrait' }}
        >
          {children}
        </TileContext.Provider>
      </div>
    </StyledSelectableTile>
  )
}

// Single checkbox implementation
const SingleCheckboxTile = React.forwardRef<
  HTMLInputElement,
  CheckboxTileProps
>(function SingleCheckboxTile(
  {
    disabled = false,
    checked,
    defaultChecked,
    orientation,
    style,
    className,
    ...props_
  },
  ref
) {
  const props = {
    ...props_,
    isDisabled: disabled,
    isSelected: checked,
    defaultSelected: defaultChecked,
  }
  const inputRef = React.useRef<HTMLInputElement>(null)
  const state = useToggleState(props)
  const { inputProps } = useCheckbox(props, state, inputRef)
  const { focusProps, isFocusVisible } = useFocusRing()

  return (
    <CheckboxTileRender
      inputProps={inputProps}
      focusProps={focusProps}
      inputRef={inputRef}
      forwardedRef={ref}
      isDisabled={disabled}
      isSelected={state.isSelected}
      isFocusVisible={isFocusVisible}
      orientation={orientation}
      className={className}
      style={style}
      role={props.role}
      aria-labelledby={props['aria-labelledby']}
    >
      {props.children}
    </CheckboxTileRender>
  )
})

// Group checkbox implementation
const GroupCheckboxTile = React.forwardRef<
  HTMLInputElement,
  CheckboxTileProps & { groupState: CheckboxGroupState }
>(function GroupCheckboxTile(
  {
    disabled = false,
    checked,
    defaultChecked,
    orientation,
    style,
    className,
    groupState,
    ...props_
  },
  ref
) {
  const props = {
    ...props_,
    isDisabled: disabled,
    isSelected: checked,
    defaultSelected: defaultChecked,
  }
  const inputRef = React.useRef<HTMLInputElement>(null)
  const { inputProps } = useCheckboxGroupItem(props, groupState, inputRef)
  const { focusProps, isFocusVisible } = useFocusRing()

  const isDisabled = groupState.isDisabled || disabled
  const isSelected = groupState.isSelected(props.value)

  return (
    <CheckboxTileRender
      inputProps={inputProps}
      focusProps={focusProps}
      inputRef={inputRef}
      forwardedRef={ref}
      isDisabled={isDisabled}
      isSelected={isSelected}
      isFocusVisible={isFocusVisible}
      orientation={orientation}
      className={className}
      style={style}
      role={props.role}
      aria-labelledby={props['aria-labelledby']}
    >
      {props.children}
    </CheckboxTileRender>
  )
})

// Wrapper that chooses the right implementation
const CheckboxTile_ = React.forwardRef<HTMLInputElement, CheckboxTileProps>(
  function CheckboxTile(props, ref) {
    const groupState = React.useContext(CheckboxGroupContext)

    if (groupState) {
      return <GroupCheckboxTile {...props} groupState={groupState} ref={ref} />
    }

    return <SingleCheckboxTile {...props} ref={ref} />
  }
)

function TileInner<Role extends Roles = 'none'>(
  { role = 'none', ...props }: TileProps<Role>,
  ref: TileRef<Role>
) {
  switch (role) {
    case 'none':
      return (
        <InteractableTile
          ref={ref as React.Ref<HTMLDivElement>}
          {...(props as Omit<InteractableTileProps, 'role'>)}
        />
      )
    case 'link':
      return (
        <LinkTile
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...(props as Omit<LinkTileProps, 'role'>)}
        />
      )
    case 'checkbox':
      return (
        <CheckboxTile_
          ref={ref as React.Ref<HTMLInputElement>}
          {...(props as Omit<CheckboxTileProps, 'role'>)}
        />
      )
    default:
      return null
  }
}

const Tile_ = React.forwardRef(TileInner) as <Role extends Roles = 'none'>(
  props: TileProps<Role> & { ref?: TileRef<Role> }
) => ReturnType<typeof TileInner>

/**
 * <NextTile role={checkbox}/> can be used as single checkbox as well as in group.
 *
 * Single
 * ```
 * <NextTile role={checkbox} value="ThomasAnderson" onChange=((value) => console.log(value) />
 * <NextTile role={checkbox} value="WhiteRabbit" onChange=((value) => console.log(value) />
 * ```
 *
 * Group
 * ```
 * <CheckboxGroup label="Matrix" onChange=((value) => console.log(value)>
 *    <NextTile role={checkbox} value="ThomasAnderson"/>
 *    <NextTile role={checkbox} value="WhiteRabbit"/>
 * </CheckboxGroup>
 * ```
 *
 * @since 11.6.0
 */
export const NextTile = addSubcomponents(
  { Actions, Content, Footer, HeroText, HeroMedia },
  Tile_
)
